import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { useIntl } from 'react-intl';

import { getCountry } from 'utils/environment';
import { IPlaceData } from 'utils/geolocation';
import { LocalStorage, StorageKeys } from 'utils/local-storage';

import { IDeliveryAddressLocation } from './save-delivery-address/types';
import { ActionStatus, DeliveryAddressModalAction } from './types';

interface DeliveryAddressModalState {
  showDeliveryAddress: boolean;
  showSaveDeliveryAddress: boolean;
  showDeliveryAddressOtherLocation: boolean;
  showSaveDeliveryAddressWithLocation: boolean;
  showDeleteDeliveryAddress: boolean;
}

export interface IDeliveryAddressModalContext {
  deliveryAddressState: DeliveryAddressModalState;
  dispatchDeliveryAddressState: Dispatch<DeliveryAddressModalAction>;
  setDeliveryAddressLocations: Dispatch<SetStateAction<string[]>>;
  setCanSaveDeliveryAddress: Dispatch<SetStateAction<boolean>>;
  deliveryAddressLocations: string[];
  canSaveDeliveryAddress: boolean;
  setDeliveryAddress: Dispatch<SetStateAction<IDeliveryAddressLocation | undefined>>;
  deliveryAddress?: IDeliveryAddressLocation;
  deliveryAddressMapFallback: IPlaceData;
  setDeliveryAddressMapFallback: Dispatch<SetStateAction<IPlaceData>>;
  setShowMapFallback: Dispatch<SetStateAction<boolean>>;
  showMapFallback: boolean;
  currentMapFallbackTab: number;
  setCurrentMapFallbackTab: (tab: number) => void;
}
export const DeliveryAddressModalContext = React.createContext<IDeliveryAddressModalContext>(
  {} as IDeliveryAddressModalContext
);
export const useDeliveryAddressModalContext = () => useContext(DeliveryAddressModalContext);

export const ADDRESS_TAB_INDEX = 0;
export const MAP_TAB_INDEX = 1;

export const DeliveryAddressModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { formatMessage } = useIntl();
  const [currentMapFallbackTab, setCurrentMapFallbackTab] = useState(ADDRESS_TAB_INDEX);
  const [canSaveDeliveryAddress, setCanSaveDeliveryAddress] = useState(true);
  const [deliveryAddressLocations, setDeliveryAddressLocations] = useState<string[]>([
    formatMessage({ id: 'home' }),
    formatMessage({ id: 'work' }),
  ]);
  const [deliveryAddress, setDeliveryAddressFn] = useState<IDeliveryAddressLocation>(
    {} as IDeliveryAddressLocation
  );
  const [deliveryAddressMapFallback, setDeliveryAddressMapFallback] = useState<IPlaceData>({
    coordinates: {
      lat: 0,
      lng: 0,
    },
    address: {
      addressLine1: '',
      city: '',
      country: getCountry(),
    },
  });

  const setDeliveryAddress = useCallback((addressDetails: IDeliveryAddressLocation) => {
    if (addressDetails?.address) {
      // Save delivery address details into local storage
      LocalStorage.setItem(StorageKeys.DELIVERY_ADDRESS, JSON.stringify(addressDetails.address));
    }

    setDeliveryAddressFn(addressDetails);
  }, []);

  const [showMapFallback, setShowMapFallback] = useState<boolean>(false);

  const saveAddressReducer = (
    state: DeliveryAddressModalState,
    action: DeliveryAddressModalAction
  ) => {
    switch (action.type) {
      case ActionStatus.CONFIRM_SAVE_DELIVERY_ADDRESS:
        return {
          ...state,
          showDeliveryAddress: false,
          showSaveDeliveryAddress: true,
          showDeliveryAddressOtherLocation: false,
          showSaveDeliveryAddressWithLocation: false,
          showDeleteDeliveryAddress: false,
        };
      case ActionStatus.CONFIRM_SAVE_DELIVERY_ADDRESS_WITH_LOCATION: {
        return {
          ...state,
          showDeliveryAddress: false,
          showSaveDeliveryAddress: false,
          showDeliveryAddressOtherLocation: false,
          showSaveDeliveryAddressWithLocation: true,
          showDeleteDeliveryAddress: false,
        };
      }
      case ActionStatus.SAVE_DELIVERY_ADDRESS_OTHER_LOCATION: {
        return {
          ...state,
          showDeliveryAddress: false,
          showSaveDeliveryAddress: false,
          showDeliveryAddressOtherLocation: true,
          showSaveDeliveryAddressWithLocation: false,
          showDeleteDeliveryAddress: false,
        };
      }
      case ActionStatus.DELIVERY_ADDRESS: {
        return {
          ...state,
          showDeliveryAddress: true,
          showSaveDeliveryAddress: false,
          showDeliveryAddressOtherLocation: false,
          showSaveDeliveryAddressWithLocation: false,
          showDeleteDeliveryAddress: false,
        };
      }
      case ActionStatus.CONFIRM_DELETE_DELIVERY_ADDRESS:
        return {
          ...state,
          showDeliveryAddress: false,
          showSaveDeliveryAddress: false,
          showDeliveryAddressOtherLocation: false,
          showSaveDeliveryAddressWithLocation: false,
          showDeleteDeliveryAddress: true,
        };
      default: {
        throw new Error('Unrecognized state transition');
      }
    }
  };

  const [deliveryAddressState, dispatchDeliveryAddressState] = useReducer(saveAddressReducer, {
    showDeliveryAddress: true,
    showSaveDeliveryAddress: false,
    showDeliveryAddressOtherLocation: false,
    showSaveDeliveryAddressWithLocation: false,
    showDeleteDeliveryAddress: false,
  });

  const value: IDeliveryAddressModalContext = useMemo(
    () => ({
      deliveryAddressState,
      dispatchDeliveryAddressState,
      setDeliveryAddressLocations,
      deliveryAddressLocations,
      deliveryAddress,
      deliveryAddressMapFallback,
      setDeliveryAddress,
      setDeliveryAddressMapFallback,
      setCanSaveDeliveryAddress,
      canSaveDeliveryAddress,
      showMapFallback,
      setShowMapFallback,
      currentMapFallbackTab,
      setCurrentMapFallbackTab,
    }),
    [
      deliveryAddressState,
      deliveryAddressLocations,
      deliveryAddress,
      deliveryAddressMapFallback,
      setDeliveryAddress,
      setDeliveryAddressMapFallback,
      canSaveDeliveryAddress,
      showMapFallback,
      currentMapFallbackTab,
    ]
  );

  return (
    <DeliveryAddressModalContext.Provider value={value}>
      {children}
    </DeliveryAddressModalContext.Provider>
  );
};
